import {
  Ship,
  UtensilsCrossed,
  Soup,
  Flame,
  Boxes,
  ChefHat,
  Award,
  PartyPopper,
  CalendarClock,
  Target,
  Sparkles,
  Layers,
  Cookie,
  MapPin,
  Users,
} from "lucide-react";

/**
 * Library config — Phase 5 (Feb 2026).
 *
 * Phase 5 deltas:
 *   - `restaurants` now uses a custom form dialog (Regular vs Specialty
 *     forms differ enough that a generic field renderer doesn't help).
 *     The `fields` list still drives the list-view columns, but the form
 *     is rendered by `RestaurantFormDialog.js`.
 *   - `theme_buffets` switched to 3 multiline-list fields (hot/cold/pastry).
 *   - New library `targets` (per-voyage success metrics, e.g. NPS Target).
 *   - `ships` is no longer a CRUD page — routed to ShipSettingsPanel.
 *
 * Field types:
 *   text | textarea | number | boolean | select | list | multilineList |
 *   time | repeatRule
 *
 * Optional per-config keys:
 *   - `customForm`: true → LibraryPage opens the matching custom dialog
 *     instead of the generic LibraryFormDialog.
 *   - `addLabel`: string → custom label for the "Add …" button.
 *   - `columnRenderer`: { columnKey: (value, row) => ReactNode }.
 */

// Phase 13.4 — Reusable Assignment block. Spread into the `fields` array
// of any library that supports the "Overall Chef in Charge / Outlet in
// Charge / Deck Phone" trio. All three pieces are optional and persist
// snapshot id+name+role for both chef refs so the labels survive a
// later deletion of the underlying lib_staff entry (drift-tolerant).
const ASSIGNMENT_FIELDS = [
  {
    key: "overall_chef_id",
    nameField: "overall_chef_name",
    roleField: "overall_chef_role",
    label: "Overall Chef in Charge",
    type: "libSelect",
    lib: "staff",
    placeholder: "Pick a staff member…",
  },
  {
    key: "outlet_chef_id",
    nameField: "outlet_chef_name",
    roleField: "outlet_chef_role",
    label: "Outlet in Charge",
    type: "libSelect",
    lib: "staff",
    placeholder: "Pick a staff member…",
  },
  {
    key: "deck_phone",
    label: "Deck Phone Number",
    type: "text",
    placeholder: "e.g. 1234",
  },
];

const LIBRARIES = {
  ships: {
    label: "Ship",
    slug: "ships",
    icon: Ship,
    description: "Default ship name for new voyages (single value)",
    fields: [
      { key: "name", label: "Ship Name", type: "text", required: true, placeholder: "e.g. Celebrity Edge" },
      { key: "notes", label: "Notes", type: "textarea", required: false, placeholder: "Optional notes" },
    ],
    columns: ["name", "notes"],
  },

  restaurants: {
    label: "Restaurants",
    slug: "restaurants",
    icon: UtensilsCrossed,
    description: "Regular dining rooms + specialty venues",
    customForm: true,
    addLabel: "Add Restaurant",
    fields: [
      // Form schema is owned by RestaurantFormDialog; these are kept for the
      // list-view column resolver only.
      { key: "name", label: "Name", type: "text" },
      { key: "type", label: "Type", type: "text" },
      { key: "enabled", label: "Enabled", type: "boolean" },
      { key: "sort_order", label: "Sort", type: "number" },
    ],
    columns: ["name", "type", "enabled", "sort_order"],
  },

  theme_buffets: {
    label: "Theme Buffets",
    slug: "theme_buffets",
    icon: Soup,
    description: "OVC theme buffets — hot/cold/pastry stations + buffet lines",
    fields: [
      { key: "name", label: "Theme Name", type: "text", required: true, placeholder: "e.g. Tuscan Night" },
      // Phase 7++++ — hot/cold/pastry are now lists of station cards
      // (matches the backend BuffetStation shape). Each station can pull
      // its dishes from lib_live_stations / lib_standard_stations or be
      // a fully custom one-off.
      { key: "hot_kitchen", label: "Hot Kitchen", type: "stations", accent: "orange" },
      { key: "cold_kitchen", label: "Cold Kitchen", type: "stations", accent: "cyan" },
      { key: "pastry", label: "Pastry", type: "stations", accent: "yellow" },
      { key: "lines", label: "Buffet Lines", type: "buffetLines" },
      // Phase 16 (F6) — Default Soup of the Day. Auto-fills the per-day
      // `soup_of_the_day` field on OVC + Crew Mess buffet meal blocks
      // when this theme is picked AND the user hasn't typed an override.
      { key: "default_soup_of_the_day", label: "Default Soup of the Day", type: "text", required: false, placeholder: "e.g. Minestrone" },
      // Phase 16 (F14) — Default Origin. Same auto-fill semantics; shows
      // next to the theme name on OVC/Crew Mess meal blocks + PDF.
      { key: "default_origin", label: "Default Origin", type: "text", required: false, placeholder: "e.g. Italian, Greek, Mediterranean" },
      { key: "notes", label: "Notes", type: "textarea", required: false },
      ...ASSIGNMENT_FIELDS,
    ],
    columns: ["name", "hot_kitchen", "cold_kitchen", "pastry", "lines"],
  },

  live_stations: {
    label: "Live Stations",
    slug: "live_stations",
    icon: Flame,
    description: "Live cooking stations for buffets",
    fields: [
      { key: "name", label: "Station Name", type: "text", required: true, placeholder: "e.g. Falafel" },
      { key: "description", label: "Description", type: "text", required: false, placeholder: "e.g. Made-to-order chickpea fritters" },
      // Phase 7++++ — dishes are now Dish objects with optional material tag.
      { key: "dishes", label: "Dishes", type: "dishList" },
      ...ASSIGNMENT_FIELDS,
    ],
    columns: ["name", "description", "dishes"],
  },

  // Phase 7+/Crew-Mess-Buffet — separate library for crew-mess live
  // stations. Identical shape to live_stations; populated either
  // manually or via "Copy to Crew Mess" on a Live Stations row.
  crew_mess_live_stations: {
    label: "Crew Mess Live Stations",
    slug: "crew_mess_live_stations",
    icon: Flame,
    description: "Live cooking stations for the Crew Mess Buffet",
    addLabel: "Add Crew Mess Live Station",
    fields: [
      { key: "name", label: "Station Name", type: "text", required: true, placeholder: "e.g. Wok" },
      { key: "description", label: "Description", type: "text", required: false, placeholder: "Short description" },
      { key: "dishes", label: "Dishes", type: "dishList" },
      ...ASSIGNMENT_FIELDS,
    ],
    columns: ["name", "description", "dishes"],
  },

  // Phase 7+++ — Standard Stations library (parallel to Live Stations,
  // not "live": pre-set buffet stations like Salad / Bread / Cheese).
  // Same shape as Live Stations; lives in a separate library so the
  // buffet line picker can offer Live vs Standard pickers.
  standard_stations: {
    label: "Standard Stations",
    slug: "standard_stations",
    icon: Boxes,
    description: "Pre-set buffet stations (Salad Bar, Bread, Cheese, …)",
    fields: [
      { key: "name", label: "Station Name", type: "text", required: true, placeholder: "e.g. Salad Bar" },
      { key: "description", label: "Description", type: "text", required: false, placeholder: "Short description shown above the dishes" },
      // Phase 7++++ — dishes are Dish objects with material tag.
      { key: "dishes", label: "Dishes", type: "dishList" },
      ...ASSIGNMENT_FIELDS,
    ],
    columns: ["name", "description", "dishes"],
  },

  // Phase 7++++ — Buffet Materials library. Names of buffet service-ware
  // (Paella Pan, Bon Chef Chafer, Elevation, Box, …) that can be tagged
  // against any dish in a buffet station / line / live station.
  buffet_materials: {
    label: "Buffet Materials",
    slug: "buffet_materials",
    icon: Layers,
    description: "Service-ware materials (Paella Pan, Bon Chef Chafer, Elevations, Boxes, …)",
    addLabel: "Add Material",
    fields: [
      { key: "name", label: "Material Name", type: "text", required: true, placeholder: "e.g. Paella Pan" },
      { key: "notes", label: "Notes", type: "textarea", required: false, placeholder: "Optional usage notes" },
    ],
    columns: ["name", "notes"],
  },

  crew_mess_themes: {
    label: "Crew Mess Themes",
    slug: "crew_mess_themes",
    icon: ChefHat,
    description: "Themed crew mess meal plans (per meal lists)",
    fields: [
      { key: "name", label: "Theme Name", type: "text", required: true, placeholder: "e.g. Italian Day" },
      { key: "breakfast", label: "Breakfast Items", type: "multilineList", placeholder: "One per line" },
      { key: "lunch", label: "Lunch Items", type: "multilineList", placeholder: "One per line" },
      { key: "dinner", label: "Dinner Items", type: "multilineList", placeholder: "One per line" },
    ],
    columns: ["name", "breakfast", "lunch", "dinner"],
  },

  chefs_specials: {
    label: "Chef's Specials",
    slug: "chefs_specials",
    icon: Award,
    description: "Signature chef special dishes (assigned per restaurant per day)",
    fields: [
      { key: "name", label: "Dish Name", type: "text", required: true, placeholder: "e.g. Lobster Thermidor" },
      // Phase 16 (F10) — description text, mirrors lib_canapes / lib_pizzas
      // semantics. Surfaced in the PDF below the dish name.
      { key: "description", label: "Description", type: "textarea", required: false, placeholder: "Optional one-liner" },
      // Phase 16 (F11) — group filter dimension. Optional. The day editor
      // uses this to filter the chef's-special picker per meal.
      { key: "group_id", label: "Group", type: "libSelect", lib: "chefs_specials_groups", required: false, placeholder: "— Ungrouped —" },
      { key: "sort_order", label: "Sort", type: "number", required: false, placeholder: "0" },
    ],
    columns: ["name", "description", "group_id"],
  },

  // Phase 16 (F11) — group library for chef's specials. Same shape as
  // special_event_groups (name + description + color).
  chefs_specials_groups: {
    label: "Chef's Special Groups",
    slug: "chefs_specials_groups",
    icon: Layers,
    description: "Groups for filtering chef's-special pickers (MDR / Buffet Lunch / Specialty …)",
    fields: [
      { key: "name", label: "Group Name", type: "text", required: true, placeholder: "e.g. Buffet Lunch" },
      { key: "description", label: "Description", type: "textarea", required: false, placeholder: "Optional one-liner" },
      { key: "color", label: "Badge Color", type: "color", required: false, placeholder: "#3B82F6" },
    ],
    columns: ["name", "color", "description"],
  },

  // Phase 16 (F10) — Bakery Specials. Same shape as chefs_specials.
  bakery_specials: {
    label: "Bakery Specials",
    slug: "bakery_specials",
    icon: Cookie,
    description: "Signature bakery items — surfaced on OVC + Crew Mess buffet meals",
    fields: [
      { key: "name", label: "Item Name", type: "text", required: true, placeholder: "e.g. Sourdough Country Loaf" },
      { key: "description", label: "Description", type: "textarea", required: false, placeholder: "Optional one-liner" },
      { key: "group_id", label: "Group", type: "libSelect", lib: "bakery_specials_groups", required: false, placeholder: "— Ungrouped —" },
      { key: "sort_order", label: "Sort", type: "number", required: false, placeholder: "0" },
    ],
    columns: ["name", "description", "group_id"],
  },

  // Phase 16 (F11) — group library for bakery specials.
  bakery_specials_groups: {
    label: "Bakery Special Groups",
    slug: "bakery_specials_groups",
    icon: Layers,
    description: "Groups for filtering bakery-special pickers (Breakfast Pastry / Lunch Bread / …)",
    fields: [
      { key: "name", label: "Group Name", type: "text", required: true, placeholder: "e.g. Breakfast Pastry" },
      { key: "description", label: "Description", type: "textarea", required: false, placeholder: "Optional one-liner" },
      { key: "color", label: "Badge Color", type: "color", required: false, placeholder: "#3B82F6" },
    ],
    columns: ["name", "color", "description"],
  },

  // Phase 16 (F5) — Pizza library. Used by the OVC Buffet "Pizza Station"
  // type. Each pizza carries name + description (rendered on PDF below
  // the pizza name).
  pizzas: {
    label: "Pizzas",
    slug: "pizzas",
    icon: Flame,
    description: "Pizza catalogue for the OVC Buffet Pizza Station",
    fields: [
      { key: "name", label: "Pizza Name", type: "text", required: true, placeholder: "e.g. Margherita" },
      { key: "description", label: "Description", type: "textarea", required: false, placeholder: "e.g. San Marzano · fior di latte · basil" },
      { key: "sort_order", label: "Sort", type: "number", required: false, placeholder: "0" },
    ],
    columns: ["name", "description"],
  },

  // Phase 16 (F5) — Pizza Set library. A saved bundle of pizza ids.
  pizza_sets: {
    label: "Pizza Sets",
    slug: "pizza_sets",
    icon: Layers,
    description: "Named bundles of pizzas — load into a Pizza Station in one click",
    fields: [
      { key: "name", label: "Set Name", type: "text", required: true, placeholder: "e.g. Classic Italian Trio" },
      { key: "pizza_ids", label: "Pizzas in this set", type: "libMultiSelect", lib: "pizzas", required: false, placeholder: "Pick pizzas to include in this bundle…" },
      { key: "sort_order", label: "Sort", type: "number", required: false, placeholder: "0" },
    ],
    columns: ["name", "pizza_ids"],
  },

  // Phase 16 (F13) — Carving catalogue (mirror of Pizzas).
  carvings: {
    label: "Carvings",
    slug: "carvings",
    icon: Flame,
    description: "Carving catalogue for the OVC Buffet Carving Station",
    fields: [
      { key: "name", label: "Carving Name", type: "text", required: true, placeholder: "e.g. Roast Beef" },
      { key: "description", label: "Description", type: "textarea", required: false, placeholder: "e.g. USDA prime · rosemary jus" },
      { key: "sort_order", label: "Sort", type: "number", required: false, placeholder: "0" },
    ],
    columns: ["name", "description"],
  },

  // Phase 16 (F13) — Carving Set library (mirror of Pizza Sets).
  carving_sets: {
    label: "Carving Sets",
    slug: "carving_sets",
    icon: Layers,
    description: "Named bundles of carvings — load into a Carving Station in one click",
    fields: [
      { key: "name", label: "Set Name", type: "text", required: true, placeholder: "e.g. Sunday Roast" },
      { key: "carving_ids", label: "Carvings in this set", type: "libMultiSelect", lib: "carvings", required: false, placeholder: "Pick carvings to include in this bundle…" },
      { key: "sort_order", label: "Sort", type: "number", required: false, placeholder: "0" },
    ],
    columns: ["name", "carving_ids"],
  },

  special_events: {
    label: "Special Events",
    slug: "special_events",
    icon: PartyPopper,
    description: "One-off special dining events",
    fields: [
      // Phase 7+++ — mirrors the Daily Events form 1:1 so the operator
      // gets a consistent shape across both event libraries.
      //   name | start_time | end_time | location | comment | repeat_rule
      { key: "name", label: "Event Name", type: "text", required: true, placeholder: "e.g. Captain's Welcome" },
      // Backend key stays `default_start` / `default_end` — these are
      // snapshotted into the per-day MealPlanSpecialEvent on selection.
      { key: "default_start", label: "Start Time", type: "time", required: false, placeholder: "HH:MM" },
      { key: "default_end", label: "End Time", type: "time", required: false, placeholder: "HH:MM" },
      { key: "default_location", label: "Location", type: "text", required: false, placeholder: "e.g. Atrium DK4" },
      // Backend key is `notes`, surfaced as "Comment" to match the
      // Daily Events form label.
      { key: "notes", label: "Comment", type: "textarea", required: false },
      // Phase 12 — optional categorisation (lib_special_event_groups).
      // Drift-tolerant: deleting the group later won't break this event.
      { key: "group_id", label: "Group", type: "libSelect", lib: "special_event_groups", required: false, placeholder: "— No group —" },
      // Auto-add toggle — same opt-in pattern as Daily Events.
      { key: "repeat_rule", label: "Repeat Rule", type: "repeatRule", required: false },
      // Phase 15 (F1) — pre-link canapés that should be SUGGESTED on
      // any voyage day this event is added to. The day editor surfaces
      // a small "Suggested canapés: …" chip row + "+ Add to today's
      // canapé function" button when the day's event has zero canape
      // items. Soft suggestion, never auto-mutates the snapshot.
      { key: "linked_canape_ids", label: "Linked Canapés (suggestions for the voyage day)", type: "libMultiSelect", lib: "canapes", required: false, placeholder: "Pick one or more canapés to pre-suggest…" },
    ],
    columns: ["name", "default_start", "default_end", "default_location"],
  },

  // Phase 12 — categorisation library for `special_events`.
  special_event_groups: {
    label: "Special Event Groups",
    slug: "special_event_groups",
    icon: PartyPopper,
    description: "Categories for grouping special events (e.g. ShipEx, Captain Events, Wine Tastings)",
    fields: [
      { key: "name", label: "Group Name", type: "text", required: true, placeholder: "e.g. ShipEx" },
      { key: "description", label: "Description", type: "textarea", required: false, placeholder: "Optional one-liner" },
      { key: "color", label: "Badge Color", type: "color", required: false, placeholder: "#3B82F6" },
    ],
    columns: ["name", "color", "description"],
  },

  daily_events: {
    label: "Daily Events",
    slug: "daily_events",
    icon: CalendarClock,
    description: "Recurring daily events with schedules",
    fields: [
      { key: "name", label: "Event Name", type: "text", required: true, placeholder: "e.g. OVC Breakfast Open" },
      { key: "default_start_time", label: "Start Time", type: "time", required: true, placeholder: "HH:MM" },
      { key: "default_end_time", label: "End Time", type: "time", required: true, placeholder: "HH:MM" },
      { key: "default_location", label: "Location", type: "text", required: false, placeholder: "e.g. OVC DK14" },
      { key: "default_comment", label: "Comment", type: "text", required: false },
      // Phase 7+++ — repeat_rule is now OPTIONAL. The UI surfaces an
      // "Auto-add to voyages" switch (in LibraryFormDialog's repeatRule
      // renderer) — OFF = manual-only, ON = unfolds the repeat picker.
      { key: "repeat_rule", label: "Repeat Rule", type: "repeatRule", required: false },
    ],
    columns: ["name", "default_start_time", "default_end_time", "default_location", "repeat_rule"],
  },

  targets: {
    label: "Targets",
    slug: "targets",
    icon: Target,
    description: "Per-voyage success metrics (NPS, food quality, …)",
    addLabel: "Add Target",
    fields: [
      { key: "name", label: "Target Name", type: "text", required: true, placeholder: "e.g. NPS Target" },
      { key: "unit", label: "Unit", type: "text", required: false, placeholder: "e.g. %  or  score" },
    ],
    columns: ["name", "unit"],
  },

  // Phase 7+ — Festivals: named timed events on a buffet meal.
  festivals: {
    label: "Festivals",
    slug: "festivals",
    icon: Sparkles,
    description: "Named timed events that run during a buffet meal (e.g. Indian Spice Festival)",
    addLabel: "Add Festival",
    fields: [
      { key: "name", label: "Festival Name", type: "text", required: true, placeholder: "e.g. Indian Spice Festival" },
      { key: "default_meal", label: "Default Meal", type: "select", required: false, options: [
        { value: "", label: "(any)" },
        { value: "breakfast", label: "Breakfast" },
        { value: "brunch", label: "Brunch" },
        { value: "lunch", label: "Lunch" },
        { value: "embarkation_lunch", label: "Embarkation Lunch" },
        { value: "tea_time", label: "Tea Time" },
        { value: "dinner", label: "Dinner" },
      ]},
      { key: "default_start", label: "Default Start", type: "time", required: false, placeholder: "HH:MM" },
      { key: "default_end", label: "Default End", type: "time", required: false, placeholder: "HH:MM" },
      { key: "dishes", label: "Dishes", type: "multilineList", placeholder: "One per line" },
      { key: "notes", label: "Notes", type: "textarea", required: false },
    ],
    columns: ["name", "default_meal", "default_start", "default_end"],
  },

  // -------------------------------------------------------------------------
  // Phase 9 — Canapé Function libraries
  // -------------------------------------------------------------------------
  production_areas: {
    key: "production_areas",
    slug: "production_areas",
    label: "Production Areas",
    singular: "Production Area",
    description: "Where canapés are prepared (Cold Kitchen, Garde Manger, Pastry, …).",
    icon: MapPin,
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "notes", label: "Notes", type: "textarea", required: false },
    ],
    columns: ["name", "notes"],
  },
  staff: {
    key: "staff",
    slug: "staff",
    label: "Staff",
    singular: "Staff Member",
    description: "People assigned as person-in-charge on canapés.",
    icon: Users,
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "role", label: "Role", type: "text", required: false,
        placeholder: "Sous Chef, Pastry Lead, …" },
      // Phase 16 (F15) — Deck Phone extension. Optional free-text so
      // the user can store "1234", "1234 / 5678" or "ext 1234". Renders
      // as a "· ext NNNN" suffix on staff pickers when set.
      { key: "deck_phone", label: "Deck Phone", type: "text", required: false,
        placeholder: "e.g. 1234, 5678 — extension or phone number" },
      { key: "notes", label: "Notes", type: "textarea", required: false },
    ],
    columns: ["name", "role", "deck_phone"],
  },
  canapes: {
    key: "canapes",
    slug: "canapes",
    label: "Canapés",
    singular: "Canapé",
    description: "Reusable canapé recipes — photo, ingredients, production area, person in charge.",
    icon: Cookie,
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "photo_url", label: "Photo", type: "photo", required: false },
      {
        key: "ingredients", label: "Ingredients", type: "ingredientList",
        required: false,
      },
      {
        key: "serving_material_id", label: "Serving Material",
        type: "libSelect", lib: "buffet_materials",
        nameField: "serving_material_name",
        required: false,
      },
      {
        key: "production_area_id", label: "Production Area",
        type: "libSelect", lib: "production_areas",
        nameField: "production_area_name",
        required: false,
      },
      {
        key: "person_in_charge_id", label: "Person in Charge",
        type: "libSelect", lib: "staff",
        nameField: "person_in_charge_name",
        required: false,
      },
      { key: "notes", label: "Notes", type: "textarea", required: false },
    ],
    columns: ["name", "production_area_name", "person_in_charge_name"],
  },
};

/** Ordered list of library keys for sidebar rendering */
export const LIBRARY_ORDER = [
  "ships",
  "restaurants",
  "theme_buffets",
  "live_stations",
  // Phase 7+/Crew-Mess-Buffet — parallel library for Crew Mess Buffet.
  "crew_mess_live_stations",
  "standard_stations",
  "buffet_materials",
  // Phase 7+/Crew-Mess-Buffet — `crew_mess_themes` deprecated. The
  // Crew Mess Buffet now picks themes from the shared `theme_buffets`
  // library. Collection kept for audit; intentionally NOT in the
  // sidebar order so it stays hidden from new pickers. Existing CRUD
  // endpoints still work for any tooling that needs them.
  "chefs_specials",
  // Phase 16 (F11) — group library for chef's specials, sits adjacent.
  "chefs_specials_groups",
  // Phase 16 (F10) — Bakery Specials + groups.
  "bakery_specials",
  "bakery_specials_groups",
  // Phase 16 (F5) — Pizza catalogue + saved bundles.
  "pizzas",
  "pizza_sets",
  // Phase 16 (F13) — Carving catalog + bundles.
  "carvings",
  "carving_sets",
  "festivals",
  "special_events",
  // Phase 12 — categorisation for Special Events. Sits right under
  // `special_events` in the sidebar for adjacency.
  "special_event_groups",
  "daily_events",
  "targets",
  // Phase 9 — Canapé Function libraries.
  "canapes",
  "production_areas",
  "staff",
];

/** Get default (empty) form values for a library */
export function getDefaultValues(libKey) {
  const config = LIBRARIES[libKey];
  if (!config) return {};
  const defaults = {};
  for (const field of config.fields) {
    switch (field.type) {
      case "boolean":
        defaults[field.key] = false;
        break;
      case "number":
        defaults[field.key] = field.min ?? 0;
        break;
      case "list":
      case "multilineList":
        defaults[field.key] = [];
        break;
      case "buffetLines":
        defaults[field.key] = [];
        break;
      // Phase 7++++ — Dish[] and BuffetStation[] both default to empty.
      case "dishList":
      case "stations":
        defaults[field.key] = [];
        break;
      case "repeatRule":
        // Phase 7+++ — when the field is optional ("Auto-add to voyages"
        // switch UX), default to null so the event is NOT auto-added.
        // When required (legacy), keep the every_day default.
        defaults[field.key] = field.required
          ? { type: "every_day", days: null }
          : null;
        break;
      // Phase 9 — Canapé Function field types.
      case "photo":
        defaults[field.key] = "";   // URL string
        break;
      case "ingredientList":
        defaults[field.key] = [];   // List<{name, amount}>
        break;
      case "libSelect":
        defaults[field.key] = null; // foreign-key id, snapshot key set separately
        if (field.nameField) defaults[field.nameField] = "";
        break;
      // Phase 15 (F1) / Phase 16 (F5 pizza_sets) — multi-foreign-key
      // chip picker. Default to empty array.
      case "libMultiSelect":
        defaults[field.key] = [];
        break;
      default:
        defaults[field.key] = "";
    }
  }
  return defaults;
}

export default LIBRARIES;
